/**
 * Auth Service — Magic Link authentication API calls
 */

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

async function authFetch(endpoint, options = {}) {
  const url = `${API_BASE_URL}${endpoint}`;

  const defaultOptions = {
    headers: { 'Content-Type': 'application/json', ...options.headers },
    credentials: 'include',
  };

  const response = await fetch(url, { ...defaultOptions, ...options });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const error = new Error(errorData.detail || `Auth error: ${response.status}`);
    error.status = response.status;
    throw error;
  }

  const text = await response.text();
  return text ? JSON.parse(text) : {};
}

const authService = {
  /**
   * Request a magic login link via email.
   * Always returns the same message (no user enumeration).
   *
   * `agbAccepted` is required for first-time registrations (#245). The backend
   * silently ignores the flag for existing accounts.
   *
   * @param {string} email
   * @param {boolean} [agbAccepted=false]
   */
  requestMagicLink: async (email, agbAccepted = false) => {
    return authFetch('/api/v1/auth/request-magic-link', {
      method: 'POST',
      body: JSON.stringify({ email, agb_accepted: agbAccepted }),
    });
  },

  /**
   * Verify a magic link token and establish a session.
   * @returns {{ success: boolean, user: object }}
   */
  verifyToken: async (token) => {
    return authFetch(`/api/v1/auth/verify?token=${encodeURIComponent(token)}`);
  },

  /**
   * Get the currently authenticated user.
   * Throws if not authenticated.
   */
  getMe: async () => {
    return authFetch('/api/v1/auth/me');
  },

  /**
   * Log out and clear the session cookie on this device only.
   */
  logout: async () => {
    return authFetch('/api/v1/auth/logout', { method: 'POST' });
  },

  /**
   * Log out on every device by revoking all outstanding sessions (SEC-07, #525).
   * Bumps the server-side session epoch so every previously issued cookie —
   * including this one — is rejected on its next request.
   */
  logoutAll: async () => {
    return authFetch('/api/v1/auth/logout-all', { method: 'POST' });
  },

  /**
   * Claim previously anonymous garden IDs for the authenticated user.
   * @param {string[]} gardenIds - Array of garden UUIDs
   */
  claimGardens: async (gardenIds) => {
    return authFetch('/api/v1/auth/claim-gardens', {
      method: 'POST',
      body: JSON.stringify({ garden_ids: gardenIds }),
    });
  },

  /**
   * Re-accept the current AGB version after a major-version bump (#245).
   * Returns the refreshed UserResponse with updated `agb_version`.
   */
  acceptAgb: async () => {
    return authFetch('/api/v1/auth/accept-agb', { method: 'POST' });
  },

  /**
   * Permanently delete the authenticated account and all its data (Art. 17, #529).
   * The backend cascades gardens + image files + magic tokens and clears the
   * session cookie. Returns a deletion summary.
   */
  deleteAccount: async () => {
    return authFetch('/api/v1/account', { method: 'DELETE' });
  },

  /**
   * Record a dashboard visit and get the previous one (#569, State G).
   * Returns `{ previous_last_seen_at, last_seen_at }`; the client uses the
   * previous value to decide whether to show the return-after-inactivity state.
   */
  touchSeen: async () => {
    return authFetch('/api/v1/account/seen', { method: 'POST' });
  },

  /**
   * Download all personal data as a JSON file (Art. 15/20, #529).
   * Triggers a browser download; does not return the payload.
   */
  exportData: async () => {
    const response = await fetch(`${API_BASE_URL}/api/v1/account/export`, {
      credentials: 'include',
    });
    if (!response.ok) {
      const error = new Error(`Export error: ${response.status}`);
      error.status = response.status;
      throw error;
    }

    const blob = await response.blob();
    const filename =
      parseFilename(response.headers.get('Content-Disposition')) ||
      'mein-gruenraum-export.json';

    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  },
};

/**
 * Extract the filename from a Content-Disposition header, if present.
 * Returns null when the header is missing or has no filename.
 */
function parseFilename(contentDisposition) {
  if (!contentDisposition) return null;
  const match = /filename="?([^"]+)"?/.exec(contentDisposition);
  return match ? match[1] : null;
}

export default authService;
